import React, { useState, useEffect } from 'react';
import { useAdminStore } from '../store/useAdminStore';

function AdminCMS() {
  const { cmsBulletins: bulletins, setCmsBulletins: setBulletins, adminIdempotencyKey, regenerateAdminIdempotencyKey, deviceId } = useAdminStore();
  
  const [olympiads, setOlympiads] = useState([]);
  const [galleryEvents, setGalleryEvents] = useState([]);
  const [galleryTabs, setGalleryTabs] = useState([]);
  const [galleryPhotos, setGalleryPhotos] = useState([]);

  // Fetch initial data on mount
  useEffect(() => {
    fetch('/api/cms/bulletins', { headers: { 'X-Requested-With': 'XMLHttpRequest' }})
      .then(r => r.json())
      .then(data => setBulletins(Array.isArray(data) ? data : []))
      .catch(() => {});

    fetch('/api/cms/olympiads', { headers: { 'X-Requested-With': 'XMLHttpRequest' }})
      .then(r => r.json())
      .then(data => setOlympiads(Array.isArray(data) ? data : []))
      .catch(() => {});

    fetch('/api/cms/gallery', { headers: { 'X-Requested-With': 'XMLHttpRequest' }})
      .then(r => r.json())
      .then(data => {
        if(Array.isArray(data)) {
          setGalleryEvents(data);
          let allTabs = [];
          let allPhotos = [];
          data.forEach(e => {
            if(e.tabs) {
              allTabs = [...allTabs, ...e.tabs];
              e.tabs.forEach(t => {
                if(t.photos) allPhotos = [...allPhotos, ...t.photos];
              });
            }
          });
          setGalleryTabs(allTabs);
          setGalleryPhotos(allPhotos);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePostBulletin = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const newBulletin = {
      title: fd.get('title'),
      type: fd.get('type'),
      description: fd.get('description'),
      link: fd.get('link') ? fd.get('link').trim() : ''
    };

    try {
      const res = await fetch('/api/admin/cms/bulletins', {
        method: 'POST',
        headers: { 'Idempotency-Key': adminIdempotencyKey, 'X-Device-Id': deviceId || '', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(newBulletin)
      });
      const data = await res.json();
      if (data.success) {
        setBulletins([data.bulletin, ...bulletins]);
        e.target.reset();
      }
    } catch {
      alert('Failed to post bulletin');
    }
  };

  const handleDeleteBulletin = async (id) => {
    if (!window.confirm("Are you sure you want to delete this bulletin?")) return;
    try {
      const res = await fetch(`/api/admin/cms/bulletins/${id}`, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': adminIdempotencyKey, 'X-Device-Id': deviceId || '', 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (res.ok) {
        setBulletins(bulletins.filter(b => b.id !== id));
      } else {
        alert('Failed to delete bulletin on server');
      }
    } catch {
      alert('Failed to delete bulletin (Network error)');
    }
  };

  // ==========================
  // OLYMPIADS MANAGER
  // ==========================
  const [olyForm, setOlyForm] = useState({ name: '', description: '', eligibility: '', syllabus: '', classes: '', exam_details: '', logo_data: '' });
  
  const handleOlympiadSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/admin/cms/olympiads', {
        method: 'POST',
        headers: { 'Idempotency-Key': adminIdempotencyKey, 'X-Device-Id': deviceId || '', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(olyForm)
      });
      
      if (res.status === 413) {
        alert("Payload Too Large: The logo image is too big. Please use an image smaller than 30kb (Base64 encoding increases file size).");
        return;
      }
      
      const data = await res.json();
      if (data.success) {
        setOlympiads([...olympiads, data.olympiad]);
        setOlyForm({ name: '', description: '', eligibility: '', syllabus: '', classes: '', exam_details: '', logo_data: '' });
        alert('Olympiad added successfully!');
      } else {
        const errorMsg = data.error || (data.errors && data.errors.map(err => err.msg).join(', ')) || 'Validation Error';
        alert(`Failed to add Olympiad: ${errorMsg}`);
      }
    } catch (err) {
      console.error(err);
      alert('Failed to add Olympiad. Network or server error.');
    }
  };

  const handleDeleteOlympiad = async (id) => {
    if (!window.confirm("Delete this Olympiad?")) return;
    try {
      const res = await fetch(`/api/admin/cms/olympiads/${id}`, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': adminIdempotencyKey, 'X-Device-Id': deviceId || '', 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (res.ok) {
        setOlympiads(olympiads.filter(o => o.id !== id));
      } else {
        const data = await res.json().catch(() => ({}));
        alert(`Failed to delete Olympiad on server. ${data.error || ''}`);
      }
    } catch {
      alert('Failed to delete Olympiad (Network error)');
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024) {
        alert("Logo must be less than 50kb due to security payload limits.");
        e.target.value = ""; // Reset input
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setOlyForm({ ...olyForm, logo_data: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  // ==========================
  // GALLERY MANAGER
  // ==========================
  const [newEventName, setNewEventName] = useState('');
  const [newTabName, setNewTabName] = useState('');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedTabId, setSelectedTabId] = useState('');
  const [selectedPhotos, setSelectedPhotos] = useState([]);

  const handleCreateEvent = async () => {
    if(!newEventName) return;
    try {
      const res = await fetch('/api/admin/cms/gallery/events', {
        method: 'POST',
        headers: { 'Idempotency-Key': adminIdempotencyKey, 'X-Device-Id': deviceId || '', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ name: newEventName })
      });
      const data = await res.json();
      if (data.success) {
        setGalleryEvents([data.event, ...galleryEvents]);
        setNewEventName('');
      }
    } catch { alert('Failed to create event'); }
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm("Delete this event and ALL its photos?")) return;
    try {
      const res = await fetch(`/api/admin/cms/gallery/events/${id}`, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': adminIdempotencyKey, 'X-Device-Id': deviceId || '', 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (res.ok) {
        setGalleryEvents(galleryEvents.filter(e => e.id !== id));
      } else {
        alert('Failed to delete event on server');
      }
    } catch { alert('Failed (Network error)'); }
  };

  const handleCreateTab = async () => {
    if(!newTabName || !selectedEventId) return;
    try {
      const res = await fetch('/api/admin/cms/gallery/tabs', {
        method: 'POST',
        headers: { 'Idempotency-Key': adminIdempotencyKey, 'X-Device-Id': deviceId || '', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ event_id: parseInt(selectedEventId), name: newTabName })
      });
      const data = await res.json();
      if (data.success) {
        setGalleryTabs([...galleryTabs, data.tab]);
        setNewTabName('');
      }
    } catch { alert('Failed to create tab'); }
  };

  const handleDeleteTab = async (id) => {
    if (!window.confirm("Delete this tab and ALL its photos?")) return;
    try {
      const res = await fetch(`/api/admin/cms/gallery/tabs/${id}`, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': adminIdempotencyKey, 'X-Device-Id': deviceId || '', 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (res.ok) {
        setGalleryTabs(galleryTabs.filter(e => e.id !== id));
      } else {
        alert('Failed to delete tab on server');
      }
    } catch { alert('Failed (Network error)'); }
  };

  const handlePhotoUpload = (e) => {
    if (!selectedTabId) {
      alert("Please select a Tab first!");
      e.target.value = "";
      return;
    }
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => f.size <= 50 * 1024);
    if (validFiles.length !== files.length) {
      alert("One or more images exceed the 50kb limit and were skipped.");
    }
    setSelectedPhotos(validFiles);
  };

  const handleUploadConfirm = async () => {
    if (selectedPhotos.length === 0) return;
    let newPhotos = [];
    for (const file of selectedPhotos) {
      const base64Data = await new Promise(resolve => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      try {
        const res = await fetch('/api/admin/cms/gallery/photos', {
          method: 'POST',
          headers: { 'Idempotency-Key': adminIdempotencyKey, 'X-Device-Id': deviceId || '', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ tab_id: parseInt(selectedTabId), image_data: base64Data, title: file.name })
        });
        const data = await res.json();
        if (data.success) {
            newPhotos.push(data.photo);
          }
      } catch {
        alert(`Failed to upload ${file.name}`);
      }
    }
    setGalleryPhotos([...newPhotos, ...galleryPhotos]);
    setSelectedPhotos([]);
    alert("Upload complete!");
  };

  const handleDeletePhoto = async (id) => {
    if (!window.confirm("Delete photo?")) return;
    try {
      const res = await fetch(`/api/admin/cms/gallery/photos/${id}`, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': adminIdempotencyKey, 'X-Device-Id': deviceId || '', 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (res.ok) {
        setGalleryPhotos(galleryPhotos.filter(e => e.id !== id));
      } else {
        alert('Failed to delete photo on server');
      }
    } catch { alert('Failed (Network error)'); }
  };


  return (
    <div className="space-y-8 pb-20">
      <div>
        <h1 className="text-3xl font-bold text-white tracking-tight">Content & Updates Hub</h1>
        <p className="text-gray-400 text-sm mt-1">Manage Bulletins, Olympiads, and Gallery Photos.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        
        {/* BULLETIN BOARD */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <span>📢</span> Home Page Bulletin Board
          </h2>
          <form onSubmit={handlePostBulletin} className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm text-gray-400 mb-1">Bulletin Title</label>
                <input name="title" required type="text" className="w-full bg-gray-950 border border-gray-800 text-white rounded p-2 outline-none focus:border-blue-500" placeholder="e.g., Results Announced for 2026!" />
              </div>
              <div className="w-1/3">
                <label className="block text-sm text-gray-400 mb-1">Type</label>
                <select name="type" className="w-full bg-gray-950 border border-gray-800 text-white rounded p-2 outline-none focus:border-blue-500">
                  <option value="RESULT">Result</option>
                  <option value="LAUNCH">Launch</option>
                  <option value="NEWS">News</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Description (Optional)</label>
              <textarea name="description" className="w-full bg-gray-950 border border-gray-800 text-white rounded p-2 h-20 outline-none focus:border-blue-500" placeholder="Add additional details here..." />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Optional Link (URL)</label>
              <input name="link" type="text" className="w-full bg-gray-950 border border-gray-800 text-white rounded p-2 outline-none focus:border-blue-500" placeholder="https://..." />
            </div>
            <button type="submit" className="bg-purple-600 hover:bg-purple-500 text-white font-bold py-2 px-6 rounded shadow-[0_0_15px_rgba(147,51,234,0.4)] transition-all">
              Broadcast Instantly
            </button>
          </form>

          <div className="mt-6 border-t border-gray-800 pt-4">
            <h3 className="text-sm font-semibold text-gray-400 mb-2">Recent Broadcasts</h3>
            <ul className="space-y-2 max-h-60 overflow-y-auto pr-2">
              {bulletins.map(b => (
                <li key={b.id} className="text-sm text-gray-300 flex justify-between items-center bg-gray-950 p-2 rounded border border-gray-800">
                  <span>[{b.type}] {b.title}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-green-500 font-mono text-xs">Live</span>
                    <button onClick={() => handleDeleteBulletin(b.id)} className="text-red-500 hover:text-red-400 font-bold px-2 py-1 bg-red-900/30 rounded text-xs transition-colors">
                      Delete
                    </button>
                  </div>
                </li>
              ))}
              {bulletins.length === 0 && <span className="text-xs text-gray-500 italic">No active bulletins.</span>}
            </ul>
          </div>
        </div>

        {/* OLYMPIADS MANAGER */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <span>🏆</span> Olympiads Manager
          </h2>
          <form onSubmit={handleOlympiadSubmit} className="space-y-3">
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-xs text-gray-400 mb-1">Name</label>
                <input required type="text" value={olyForm.name} onChange={e=>setOlyForm({...olyForm, name: e.target.value})} className="w-full bg-gray-950 border border-gray-800 text-white rounded p-1.5 text-sm outline-none focus:border-blue-500" placeholder="e.g. MCO" />
              </div>
              <div className="w-1/3">
                <label className="block text-xs text-gray-400 mb-1">Classes</label>
                <input type="text" value={olyForm.classes} onChange={e=>setOlyForm({...olyForm, classes: e.target.value})} className="w-full bg-gray-950 border border-gray-800 text-white rounded p-1.5 text-sm outline-none focus:border-blue-500" placeholder="e.g. 6-12" />
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-xs text-gray-400 mb-1">Description</label>
                <textarea value={olyForm.description} onChange={e=>setOlyForm({...olyForm, description: e.target.value})} className="w-full bg-gray-950 border border-gray-800 text-white rounded p-1.5 text-sm h-12 outline-none focus:border-blue-500" />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-400 mb-1">Eligibility</label>
                <textarea value={olyForm.eligibility} onChange={e=>setOlyForm({...olyForm, eligibility: e.target.value})} className="w-full bg-gray-950 border border-gray-800 text-white rounded p-1.5 text-sm h-12 outline-none focus:border-blue-500" />
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-xs text-gray-400 mb-1">Syllabus</label>
                <textarea value={olyForm.syllabus} onChange={e=>setOlyForm({...olyForm, syllabus: e.target.value})} className="w-full bg-gray-950 border border-gray-800 text-white rounded p-1.5 text-sm h-12 outline-none focus:border-blue-500" />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-400 mb-1">Exam Details</label>
                <textarea value={olyForm.exam_details} onChange={e=>setOlyForm({...olyForm, exam_details: e.target.value})} className="w-full bg-gray-950 border border-gray-800 text-white rounded p-1.5 text-sm h-12 outline-none focus:border-blue-500" />
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-400 mb-1">Logo Upload <span className="text-red-400">(Max 50kb)</span></label>
              <input type="file" accept="image/*" onChange={handleLogoUpload} className="w-full text-sm text-gray-400 file:mr-4 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-gray-800 file:text-white hover:file:bg-gray-700 cursor-pointer" />
              {olyForm.logo_data && <img src={olyForm.logo_data} alt="preview" className="mt-2 h-10 rounded" />}
            </div>

            <button type="submit" className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold py-2 px-6 rounded shadow-[0_0_15px_rgba(37,99,235,0.4)] transition-all">
              Add Olympiad
            </button>
          </form>

          <div className="mt-6 border-t border-gray-800 pt-4">
            <ul className="space-y-2 max-h-40 overflow-y-auto pr-2">
              {olympiads.map(o => (
                <li key={o.id} className="text-sm text-gray-300 flex justify-between items-center bg-gray-950 p-2 rounded border border-gray-800">
                  <div className="flex items-center gap-2">
                    {o.logo_data && <img src={o.logo_data} className="w-6 h-6 object-cover rounded-full" />}
                    <span>{o.name}</span>
                  </div>
                  <button onClick={() => handleDeleteOlympiad(o.id)} className="text-red-500 hover:text-red-400 font-bold px-2 py-1 bg-red-900/30 rounded text-xs transition-colors">
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

      </div>
      
      {/* GALLERY MANAGER */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <span>🖼️</span> Gallery Manager
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* Events Col */}
          <div className="bg-gray-950 border border-gray-800 rounded-lg p-4">
            <h3 className="text-white font-semibold mb-3 border-b border-gray-800 pb-2">1. Events</h3>
            <div className="flex gap-2 mb-4">
              <input value={newEventName} onChange={e=>setNewEventName(e.target.value)} type="text" placeholder="e.g. MCO 2026" className="w-full bg-gray-900 border border-gray-700 text-white rounded p-1.5 text-sm outline-none" />
              <button onClick={handleCreateEvent} className="bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded text-sm">+</button>
            </div>
            <ul className="space-y-2">
              {galleryEvents.map(e => (
                <li key={e.id} className={`flex justify-between items-center p-2 rounded cursor-pointer border ${selectedEventId == e.id ? 'bg-blue-900/30 border-blue-500 text-blue-400' : 'bg-gray-900 border-gray-800 text-gray-300'}`} onClick={() => setSelectedEventId(e.id)}>
                  <span className="text-sm font-medium">{e.name}</span>
                  <button onClick={(ev) => { ev.stopPropagation(); handleDeleteEvent(e.id); }} className="text-red-500 hover:text-red-400 text-xs">✕</button>
                </li>
              ))}
            </ul>
          </div>

          {/* Tabs Col */}
          <div className="bg-gray-950 border border-gray-800 rounded-lg p-4 transition-opacity" style={{ opacity: selectedEventId ? 1 : 0.4, pointerEvents: selectedEventId ? 'auto' : 'none' }}>
            <h3 className="text-white font-semibold mb-3 border-b border-gray-800 pb-2">2. Tabs for Selected Event</h3>
            <div className="flex gap-2 mb-4">
              <input value={newTabName} onChange={e=>setNewTabName(e.target.value)} type="text" placeholder="e.g. Award Ceremony" className="w-full bg-gray-900 border border-gray-700 text-white rounded p-1.5 text-sm outline-none" />
              <button onClick={handleCreateTab} className="bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded text-sm">+</button>
            </div>
            <ul className="space-y-2">
              {galleryTabs.filter(t => t.event_id == selectedEventId).map(t => (
                <li key={t.id} className={`flex justify-between items-center p-2 rounded cursor-pointer border ${selectedTabId == t.id ? 'bg-purple-900/30 border-purple-500 text-purple-400' : 'bg-gray-900 border-gray-800 text-gray-300'}`} onClick={() => setSelectedTabId(t.id)}>
                  <span className="text-sm font-medium">{t.name}</span>
                  <button onClick={(ev) => { ev.stopPropagation(); handleDeleteTab(t.id); }} className="text-red-500 hover:text-red-400 text-xs">✕</button>
                </li>
              ))}
            </ul>
          </div>

          {/* Photos Col */}
          <div className="bg-gray-950 border border-gray-800 rounded-lg p-4 transition-opacity" style={{ opacity: selectedTabId ? 1 : 0.4, pointerEvents: selectedTabId ? 'auto' : 'none' }}>
            <h3 className="text-white font-semibold mb-3 border-b border-gray-800 pb-2 flex justify-between items-center">
              3. Upload Photos <span className="text-[10px] text-red-400 font-normal">Max 50kb/pic</span>
            </h3>
            <div className="mb-4 space-y-2">
              <input type="file" multiple accept="image/*" onChange={handlePhotoUpload} className="w-full text-sm text-gray-400 file:mr-4 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-gray-800 file:text-white hover:file:bg-gray-700 cursor-pointer" />
              {selectedPhotos.length > 0 && (
                <button onClick={handleUploadConfirm} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-1.5 px-3 rounded text-sm transition-colors shadow-lg">
                  Upload {selectedPhotos.length} Selected Photos
                </button>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2 h-48 overflow-y-auto">
              {galleryPhotos.filter(p => p.tab_id == selectedTabId).map(p => (
                <div key={p.id} className="relative group rounded overflow-hidden border border-gray-800 aspect-square bg-gray-900">
                  <img src={p.image_data} alt={p.title} className="w-full h-full object-cover" />
                  <button onClick={() => handleDeletePhoto(p.id)} className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">✕</button>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

    </div>
  );
}

export default AdminCMS;
